import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DisputesQueueDetailsInformation } from './DisputesQueueDetailsInformation';
import { DisputesQueueDetailsList } from './DisputesQueueDetailsList';
import { MRT_RowSelectionState } from 'material-react-table';
import { ArchiveDisputeModal } from '../ArchiveDisputeModal';
import { useDisputesContext } from '../../../../store/disputes/disputes-context';
import { PageTitle } from '../../../../layout/core';
import { fetchDisputeDetails, setDisputeQueueItemDetailsToDefault } from '../../../../store/disputes/actions/disputes';
import { IModalWindow, ModalWindow } from '../../../modal/ModalWindow';
import { GoBackBtn } from '../../../header/GoBackBtn';
import { BUTTON_TYPE, ButtonComponent } from '../../../ButtonComponent';
import { Spinner } from '../../../Spinner';
import { ITransactionsData } from '../../../../store/payments/payments-interfaces-helper';
import { getParsedJsonData } from '../../../../utils/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArchive } from '@fortawesome/free-solid-svg-icons';
import { ROUTES } from '../../../../routing/routesEnums';

export const DisputesQueueDetails: FC = () => {
	const { t } = useTranslation();
	const { state } = useLocation();
	const navigate = useNavigate();
	const { disputeQueueItemId } = useParams<{ disputeQueueItemId: string }>();
	const { disputesContextState, dispatch } = useDisputesContext();
	const { isDisputeDetailsLoading, disputeDetails } = disputesContextState;

	const [checkedItem, setCheckedItem] = useState<ITransactionsData | null>(null);
	const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
	const [isArchiveModalOpen, setIsArchiveModalOpen] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const backUrl = state?.disputeQueueItem?.disputeQueueId ?
		`${ROUTES.DISPUTES}${ROUTES.DISPUTES_QUEUE}/${state?.disputeQueueItem?.disputeQueueId}${state?.disputeQueueItem?.search || ''}`
		: `${ROUTES.DISPUTES}${ROUTES.DISPUTES_ARCHIVED}${state?.search || ''}`;

	useEffect(() => {
		void fetchDisputeDetails(dispatch, disputeQueueItemId);
	}, []);

	useEffect(() => {
		return (): void => {
			setDisputeQueueItemDetailsToDefault(dispatch);
		};
	}, []);

	const closeArchiveModal = (): void => setIsArchiveModalOpen(false);
	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const archivedSuccess = ():void => {
		closeArchiveModal();
		setModalState({
			isOpen: true,
			modalTitle: 'Archiveting dispute',
			modalText: 'Archived successfully',
			confirmButtonText: 'Ok',
			minHeight: 100,
			backdrop: 'static',
			onConfirm: (): void => {
				closeModal();
				navigate(`${ROUTES.DISPUTES}${ROUTES.DISPUTES_QUEUES}${state?.disputeQueuesSearch || ''}`);
			},
			onClose: (): void => {
				closeModal();
				navigate(`${ROUTES.DISPUTES}${ROUTES.DISPUTES_QUEUES}${state?.disputeQueuesSearch || ''}`);
			},
		});
	};

	const archivedError = ():void => {
		closeArchiveModal();
		setModalState({
			isOpen: true,
			modalTitle: 'Archiveting dispute',
			modalText: 'Archiveting failed',
			confirmButtonText: 'Ok',
			minHeight: 100,
			onConfirm: closeModal,
			onClose: closeModal
		});
	};

	if (isDisputeDetailsLoading) {
		return (
			<div className="card w-100 h-100">
				<div className="card-body">
					<Spinner
						pageLoader={true}
					/>
				</div>
			</div>
		);
	}

	const openMetaModal = (data: string): void => {
		const metaData = getParsedJsonData(data);
		setModalState({
			isOpen: true,
			modalTitle: 'Dispute queue item data',
			contentClassName: data ? 'd-block pb-1 log-scrollbar' : 'd-flex',
			modalText: !data && 'There is no information',
			isRightSideModal: true,
			content: data && (
				<div className="sense-script m-6">
					<pre className="code-area p-5">
						{metaData}
					</pre>
				</div>
			),
			onClose: closeModal
		});
	};

	return (
		<>
			<PageTitle
				breadcrumbs={[
					{
						title: t('MENU.DISPUTES'),
						path: '/disputes/queues',
						isSeparator: false,
						isActive: false
					},
					{
						title: '',
						path: '',
						isSeparator: true,
						isActive: false
					}
				]}
			>
				{disputeQueueItemId}
			</PageTitle>

			<div className="d-flex align-items-center justify-content-between mb-5">
				<GoBackBtn
					path={backUrl}
					state={state}
				/>
				<div className="d-flex align-items-center">
					<ButtonComponent
						buttonType={BUTTON_TYPE.WHITE}
						additionalClass="me-5"
						onButtonClick={(): void => setIsArchiveModalOpen(true)}
					>
						<>
							<span className="me-2">Archive</span>
							<FontAwesomeIcon icon={faFileArchive} />
						</>
					</ButtonComponent>

					<ButtonComponent
						buttonType={BUTTON_TYPE.WHITE}
						onButtonClick={(): void => openMetaModal(disputeDetails?.meta)}
					>
						<>
							<span>Meta</span>
							<span className="no-letter-spacing fs-8 ms-1">{`{JSON}`}</span>
						</>
					</ButtonComponent>
				</div>
			</div>

			<DisputesQueueDetailsInformation/>

			<DisputesQueueDetailsList
				checkedItem={checkedItem}
				setCheckedItem={setCheckedItem}
				rowSelection={rowSelection}
				setRowSelection={setRowSelection}
			/>

			<ModalWindow
				{...modalState}
			/>

			<ModalWindow
				isOpen={isArchiveModalOpen}
				onClose={closeArchiveModal}
				modalTitle="Archivating dispute"
				minHeight={150}
				backdrop="static"
				content={
					<ArchiveDisputeModal
						closeModal={closeArchiveModal}
						onSuccess={archivedSuccess}
						onError={archivedError}
					/>
				}
			/>

		</>
	);
};
